// AGC: Emergency Withdraw Test Page for StakingRevenueShareV4
// AGC: V4 Simplified Flow:
// AGC: 1. Signer proposes (token, amount, wallet from signers, voting duration)
// AGC: 2. Signer approves → voting countdown starts
// AGC: 3. Voting ends → 51% YES = pass, execute immediately
// AGC: NO dual-signer bypass - always requires staker vote
import React, { useState, useEffect } from 'react'
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt, useChainId } from 'wagmi'
import { formatEther, formatUnits, parseEther, parseUnits } from 'viem'
import { logger } from '../utils/logger'

// AGC: Hardcoded signer addresses from StakingRevenueShare contract
const FIXED_SIGNER_1 = '0xc3594084C18B79c956Ee1A16FB150DEEC5BD0b52'
const FIXED_SIGNER_2 = '0xB5F79EB3b94D4Be8B5f65070CD4408d22F09A99E'

// AGC: V4 TokenType enum values (matches contract - no CUSTOM in V4)
enum TokenType {
  ETH = 0,
  OGB = 1,
  USDC = 2,
  FARMPRO = 3,
}

// AGC: Token labels for UI
const TOKEN_LABELS: Record<TokenType, string> = {
  [TokenType.ETH]: 'ETH',
  [TokenType.OGB]: 'OGB',
  [TokenType.USDC]: 'USDC',
  [TokenType.FARMPRO]: 'FarmPro',
}

// AGC: StakingRevenueShareV4 ABI
const STAKING_REVENUE_SHARE_V4_ABI = [
  {
    name: 'getBalances',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'ethBalance', type: 'uint256' },
      { name: 'ogbBalance', type: 'uint256' },
      { name: 'usdcBalance', type: 'uint256' },
      { name: 'farmProBalance', type: 'uint256' },
    ],
  },
  {
    name: 'getSigners',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'signer1', type: 'address' },
      { name: 'signer2', type: 'address' },
    ],
  },
  {
    name: 'proposeWithdrawal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'recipient', type: 'address' },
      { name: 'tokenType', type: 'uint8' },
      { name: 'amount', type: 'uint256' },
      { name: 'votingDurationMinutes', type: 'uint256' },
    ],
    outputs: [{ name: 'proposalId', type: 'uint256' }],
  },
  {
    name: 'approveWithdrawal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'proposalId', type: 'uint256' },
      { name: 'votingDurationMinutes', type: 'uint256' },
    ],
    outputs: [],
  },
  {
    name: 'voteOnWithdrawal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'proposalId', type: 'uint256' },
      { name: 'voteYes', type: 'bool' },
    ],
    outputs: [],
  },
  {
    name: 'executeWithdrawal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [],
  },
  {
    name: 'cancelProposal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [],
  },
  {
    name: 'getProposal',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [
      { name: 'recipient', type: 'address' },
      { name: 'proposedAt', type: 'uint256' },
      { name: 'votingDeadline', type: 'uint256' },
      { name: 'approved', type: 'bool' },
      { name: 'executed', type: 'bool' },
      { name: 'cancelled', type: 'bool' },
      { name: 'stakerVotesYes', type: 'uint256' },
      { name: 'stakerVotesNo', type: 'uint256' },
      { name: 'tokenType', type: 'uint8' },
      { name: 'amount', type: 'uint256' },
    ],
  },
  {
    name: 'canExecuteProposal',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [
      { name: 'canExecute', type: 'bool' },
      { name: 'reason', type: 'string' },
    ],
  },
  {
    name: 'isVotePassed',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'getEligibleVotingStake',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'lastStakeBalance',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: '', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'hasVoted',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: '', type: 'uint256' },
      { name: '', type: 'address' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'nextProposalId',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

interface EmergencyWithdrawProps {
  onBack: () => void
}

// AGC: Proposal Item Component (for collapsible proposal history) - Shared between both contracts
interface ProposalItemProps {
  proposalId: bigint
  revenueShareAddress: `0x${string}`
  stakingManagerAddress: `0x${string}`
  chainId: number
  executionTxHash: string | null
  isExpanded: boolean
  onToggleExpand: () => void
}

const ProposalItem: React.FC<ProposalItemProps> = ({
  proposalId,
  revenueShareAddress,
  stakingManagerAddress,
  chainId,
  executionTxHash,
  isExpanded,
  onToggleExpand,
}) => {
  // AGC: Read proposal data from StakingRevenueShare
  const { data: proposalDataRevenueShare } = useReadContract({
    address: revenueShareAddress,
    abi: STAKING_REVENUE_SHARE_V4_ABI,
    functionName: 'getProposal',
    args: [proposalId],
    query: {
      enabled: true,
      refetchInterval: 5000,
    },
  })

  // AGC: StakingManager ABI for getProposal (different structure)
  const STAKING_MANAGER_GET_PROPOSAL_ABI = [
    {
      name: 'getProposal',
      type: 'function',
      stateMutability: 'view',
      inputs: [{ name: 'proposalId', type: 'uint256' }],
      outputs: [
        { name: 'recipient', type: 'address' },
        { name: 'proposedAt', type: 'uint256' },
        { name: 'votingDurationMinutes', type: 'uint256' },
        { name: 'votingDeadline', type: 'uint256' },
        { name: 'approvedBySigner1', type: 'bool' },
        { name: 'approvedBySigner2', type: 'bool' },
        { name: 'executed', type: 'bool' },
        { name: 'cancelled', type: 'bool' },
        { name: 'stakerVotesYes', type: 'uint256' },
        { name: 'stakerVotesNo', type: 'uint256' },
        { name: 'tokenType', type: 'uint8' },
        { name: 'amount', type: 'uint256' },
      ],
    },
    {
      name: 'isVotePassed',
      type: 'function',
      stateMutability: 'view',
      inputs: [{ name: 'proposalId', type: 'uint256' }],
      outputs: [{ name: '', type: 'bool' }],
    },
  ] as const

  // AGC: Read proposal data from StakingManager (shared proposal IDs)
  const { data: proposalDataStakingManager } = useReadContract({
    address: stakingManagerAddress,
    abi: STAKING_MANAGER_GET_PROPOSAL_ABI,
    functionName: 'getProposal',
    args: [proposalId],
    query: {
      enabled: true,
      refetchInterval: 5000,
    },
  })

  // AGC: Determine which contract has the proposal and get the data
  const proposalData = proposalDataRevenueShare || proposalDataStakingManager
  const contractAddress = proposalDataRevenueShare ? revenueShareAddress : stakingManagerAddress
  const contractType = proposalDataRevenueShare ? 'StakingRevenueShare' : 'StakingManager'
  const isStakingManager = !!proposalDataStakingManager

  // AGC: Check if vote passed
  const { data: votePassed } = useReadContract({
    address: contractAddress,
    abi: isStakingManager ? STAKING_MANAGER_GET_PROPOSAL_ABI : STAKING_REVENUE_SHARE_V4_ABI,
    functionName: 'isVotePassed',
    args: [proposalId],
    query: {
      enabled: !!proposalData,
    },
  })

  if (!proposalData) {
    return null
  }

  // AGC: Parse proposal data based on contract type
  const proposal = isStakingManager
    ? {
        // AGC: StakingManager structure
        recipient: proposalData[0] as string,
        proposedAt: proposalData[1] as bigint,
        votingDurationMinutes: proposalData[2] as bigint,
        votingDeadline: proposalData[3] as bigint,
        approvedBySigner1: proposalData[4] as boolean,
        approvedBySigner2: proposalData[5] as boolean,
        approved: (proposalData[4] as boolean) && (proposalData[5] as boolean), // AGC: Both must approve
        executed: proposalData[6] as boolean,
        cancelled: proposalData[7] as boolean,
        stakerVotesYes: BigInt(proposalData[8]?.toString() || '0'), // AGC: Ensure BigInt conversion
        stakerVotesNo: BigInt(proposalData[9]?.toString() || '0'), // AGC: Ensure BigInt conversion
        tokenType: proposalData[10] as number,
        amount: proposalData[11] as bigint,
      }
    : {
        // AGC: StakingRevenueShare structure
        recipient: proposalData[0] as string,
        proposedAt: proposalData[1] as bigint,
        votingDeadline: proposalData[2] as bigint,
        approved: proposalData[3] as boolean,
        executed: proposalData[4] as boolean,
        cancelled: proposalData[5] as boolean,
        stakerVotesYes: BigInt(proposalData[6]?.toString() || '0'), // AGC: Ensure BigInt conversion
        stakerVotesNo: BigInt(proposalData[7]?.toString() || '0'), // AGC: Ensure BigInt conversion
        tokenType: proposalData[8] as number,
        amount: proposalData[9] as bigint,
      }

  // AGC: Calculate vote percentages with proper BigInt handling
  const stakerVotesYes = BigInt(proposal.stakerVotesYes?.toString() || '0')
  const stakerVotesNo = BigInt(proposal.stakerVotesNo?.toString() || '0')
  const totalVotes = stakerVotesYes + stakerVotesNo
  const yesPercent = totalVotes > 0n ? Number((stakerVotesYes * 100n) / totalVotes) : 0

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden">
      {/* AGC: Proposal Header (Clickable to expand) */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between px-3 py-2 bg-slate-800/50 hover:bg-slate-700/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">
            {contractType === 'StakingRevenueShare' ? 'SRS' : 'SM'} #{proposalId.toString()}
          </span>
          <span className={`text-xs font-semibold ${
            proposal.executed ? 'text-green-400' : 
            proposal.cancelled ? 'text-red-400' : 
            'text-yellow-400'
          }`}>
            {proposal.executed ? '✅ Executed' : proposal.cancelled ? '❌ Cancelled' : '⏳ Active'}
          </span>
          {proposal.executed && executionTxHash && (
            <a
              href={`${chainId === 84532 ? 'https://sepolia.basescan.org' : 'https://basescan.org'}/tx/${executionTxHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              🔗
            </a>
          )}
        </div>
        <span className="text-xs text-slate-400">{isExpanded ? '▼' : '▶'}</span>
      </button>
      
      {/* AGC: Proposal Details (shown when expanded) */}
      {isExpanded && (
        <div className="px-3 py-2 bg-slate-900/50 border-t border-slate-700">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-slate-500">Token</p>
              <p className="font-bold text-white">{TOKEN_LABELS[proposal.tokenType as TokenType] || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-slate-500">Amount</p>
              <p className="font-bold text-white">{proposal.amount === 0n ? 'All' : formatEther(proposal.amount)}</p>
            </div>
            <div>
              <p className="text-slate-500">Recipient</p>
              <p className="font-mono text-xs text-slate-300 break-all">
                {proposal.recipient.slice(0, 6)}...{proposal.recipient.slice(-4)}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Vote Status</p>
              <p className={votePassed ? 'text-green-400' : 'text-yellow-400'}>
                {yesPercent}% YES {votePassed ? '✅' : ''}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Approved</p>
              <p className={proposal.approved ? 'text-green-400' : 'text-slate-500'}>
                {proposal.approved ? '✅ Yes' : '⏳ Pending'}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Proposed</p>
              <p className="text-xs text-slate-400">
                {new Date(Number(proposal.proposedAt) * 1000).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const EmergencyWithdrawStakingRevenueShare: React.FC<EmergencyWithdrawProps> = ({ onBack }) => {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { writeContractAsync, isPending } = useWriteContract()
  const [proposalId, setProposalId] = useState<bigint | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [executionTxHash, setExecutionTxHash] = useState<string | null>(null)
  const [shouldAutoLoadProposal, setShouldAutoLoadProposal] = useState(true) // AGC: Flag to prevent auto-loading when user wants to start new proposal
  
  // AGC: Store tx hashes for all proposals (persisted in localStorage - shared with StakingManager)
  const [proposalTxHashes, setProposalTxHashes] = useState<Record<string, string>>(() => {
    try {
      const stored = localStorage.getItem('emergencyWithdrawProposalTxHashes')
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })
  
  // AGC: Proposal history state
  const [isProposalHistoryExpanded, setIsProposalHistoryExpanded] = useState(false)
  const [expandedProposals, setExpandedProposals] = useState<Set<bigint>>(new Set())
  
  // AGC: V4 Proposal form state
  const [tokenType, setTokenType] = useState<TokenType>(TokenType.ETH)
  const [withdrawAmount, setWithdrawAmount] = useState<string>('0')
  const [selectedRecipient, setSelectedRecipient] = useState<string>(FIXED_SIGNER_1)
  const [votingDurationMinutes, setVotingDurationMinutes] = useState<string>('5')

  // AGC: Get contract address from environment variable
  const contractAddress = (import.meta.env.VITE_STAKING_REVENUE_SHARE_ADDRESS || '0x8Ca66F5ba3Daa478a45814b5b59eD21750Dc1804') as `0x${string}`
  
  // AGC: StakingManager address for fallback stake check and shared proposal history
  // AGC: Updated to V5 (0xB5F6Dd1334d6a317Ec314820403b7f1a3fF71a65) - migrated from V4
  const stakingManagerAddress = (import.meta.env.VITE_STAKING_MANAGER_ADDRESS || '0xB5F6Dd1334d6a317Ec314820403b7f1a3fF71a65') as `0x${string}`

  // AGC: Read ETH balance directly from provider (more reliable than getBalances which fails on FARMPRO)
  const { data: ethBalanceRaw, refetch: refetchEthBalance } = useReadContract({
    address: contractAddress,
    abi: [{
      name: 'receive',
      type: 'receive',
      stateMutability: 'payable',
    }] as const,
    functionName: undefined as any,
    query: {
      enabled: false, // Disabled - we'll use useBalance instead
    },
  })
  
  // AGC: Use wagmi's useBalance for ETH
  const { data: contractEthBalance, refetch: refetchContractEthBalance } = useReadContract({
    address: undefined as any, // Placeholder - we'll compute below
    abi: [] as const,
    functionName: undefined as any,
    query: { enabled: false },
  })

  // AGC: Read OGB balance
  const OGB_TOKEN_ADDRESS = (import.meta.env.VITE_OGB_TOKEN_ADDRESS || '0x0a862e9bE2716162f14BE1cb500b29354ED3D434') as `0x${string}`
  const USDC_ADDRESS = (import.meta.env.VITE_USDC_ADDRESS || '0x036CbD53842c5426634e7929541eC2318f3dCF7e') as `0x${string}`
  
  const ERC20_BALANCE_ABI = [{
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  }] as const

  const { data: ogbBalanceRaw, refetch: refetchOgbBalance } = useReadContract({
    address: OGB_TOKEN_ADDRESS,
    abi: ERC20_BALANCE_ABI,
    functionName: 'balanceOf',
    args: [contractAddress],
    query: { refetchInterval: 5000 },
  })

  const { data: usdcBalanceRaw, refetch: refetchUsdcBalance } = useReadContract({
    address: USDC_ADDRESS,
    abi: ERC20_BALANCE_ABI,
    functionName: 'balanceOf',
    args: [contractAddress],
    query: { refetchInterval: 5000 },
  })

  // AGC: Read ETH balance using getBalance via a custom hook pattern
  const [ethBalanceValue, setEthBalanceValue] = useState<bigint>(0n)
  
  useEffect(() => {
    const fetchEthBalance = async () => {
      try {
        // AGC: Use fetch to call JSON-RPC directly for eth_getBalance
        const response = await fetch(import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'eth_getBalance',
            params: [contractAddress, 'latest'],
            id: 1,
          }),
        })
        const data = await response.json()
        if (data.result) {
          setEthBalanceValue(BigInt(data.result))
        }
      } catch (err) {
        logger.warn('Failed to fetch ETH balance:', err)
      }
    }
    
    fetchEthBalance()
    const interval = setInterval(fetchEthBalance, 5000)
    return () => clearInterval(interval)
  }, [contractAddress])

  // AGC: Refetch all balances
  const refetchBalances = async () => {
    await Promise.all([
      refetchOgbBalance(),
      refetchUsdcBalance(),
    ])
    // AGC: Re-fetch ETH balance
    try {
      const response = await fetch(import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'eth_getBalance',
          params: [contractAddress, 'latest'],
          id: 1,
        }),
      })
      const data = await response.json()
      if (data.result) {
        setEthBalanceValue(BigInt(data.result))
      }
    } catch (err) {
      logger.warn('Failed to refetch ETH balance:', err)
    }
  }

  // AGC: Read next proposal ID from StakingRevenueShare
  const { data: nextProposalIdRevenueShare, refetch: refetchNextProposalIdRevenueShare } = useReadContract({
    address: contractAddress,
    abi: STAKING_REVENUE_SHARE_V4_ABI,
    functionName: 'nextProposalId',
    query: {
      refetchInterval: 3000,
    },
  })

  // AGC: Read next proposal ID from StakingManager (shared proposal history)
  const { data: nextProposalIdStakingManager, refetch: refetchNextProposalIdStakingManager } = useReadContract({
    address: stakingManagerAddress,
    abi: STAKING_REVENUE_SHARE_V4_ABI, // AGC: Same ABI structure for nextProposalId
    functionName: 'nextProposalId',
    query: {
      refetchInterval: 3000,
    },
  })

  // AGC: Each contract maintains its own counter - no unified counter needed

  // AGC: Refetch both nextProposalIds
  const refetchNextProposalId = async () => {
    await Promise.all([
      refetchNextProposalIdRevenueShare(),
      refetchNextProposalIdStakingManager(),
    ])
  }

  // AGC: Read current proposal
  const { data: proposal, refetch: refetchProposal } = useReadContract({
    address: contractAddress,
    abi: STAKING_REVENUE_SHARE_V4_ABI,
    functionName: 'getProposal',
    args: proposalId !== null ? [proposalId] : undefined,
    query: {
      enabled: proposalId !== null,
      refetchInterval: 5000,
    },
  })

  // AGC: Check if proposal can be executed
  const { data: canExecuteData, refetch: refetchCanExecute } = useReadContract({
    address: contractAddress,
    abi: STAKING_REVENUE_SHARE_V4_ABI,
    functionName: 'canExecuteProposal',
    args: proposalId !== null ? [proposalId] : undefined,
    query: {
      enabled: proposalId !== null,
      refetchInterval: 5000,
    },
  })

  // AGC: Check if vote passed
  const { data: votePassed, refetch: refetchVotePassed } = useReadContract({
    address: contractAddress,
    abi: STAKING_REVENUE_SHARE_V4_ABI,
    functionName: 'isVotePassed',
    args: proposalId !== null ? [proposalId] : undefined,
    query: {
      enabled: proposalId !== null,
      refetchInterval: 5000,
    },
  })

  // AGC: StakingManager ABI for stake check
  const STAKING_MANAGER_ABI = [
    {
      name: 'getStakingInfo',
      type: 'function',
      stateMutability: 'view',
      inputs: [{ name: 'user', type: 'address' }],
      outputs: [
        { name: 'stakedBalance', type: 'uint256' },
        { name: 'stakingScore', type: 'uint256' },
        { name: 'tier', type: 'uint8' },
        { name: 'pendingUnstakes', type: 'uint256' },
      ],
    },
  ] as const

  // AGC: Get user's stake from StakingManager
  const { data: stakingInfo } = useReadContract({
    address: stakingManagerAddress,
    abi: STAKING_MANAGER_ABI,
    functionName: 'getStakingInfo',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address,
    },
  })

  // AGC: Get user's stake balance from StakingRevenueShare (for voting)
  const { data: userStakeBalanceFromRevenueShare } = useReadContract({
    address: contractAddress,
    abi: STAKING_REVENUE_SHARE_V4_ABI,
    functionName: 'lastStakeBalance',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address,
    },
  })

  // AGC: Check if user has voted
  const { data: hasVoted } = useReadContract({
    address: contractAddress,
    abi: STAKING_REVENUE_SHARE_V4_ABI,
    functionName: 'hasVoted',
    args: proposalId !== null && address ? [proposalId, address] : undefined,
    query: {
      enabled: proposalId !== null && !!address,
    },
  })

  // AGC: Use stake from StakingManager as PRIMARY source
  const userStakeFromManager = stakingInfo ? (stakingInfo as any)[0] as bigint : 0n
  const userStakeBalance = userStakeFromManager > 0n ? userStakeFromManager : (userStakeBalanceFromRevenueShare || 0n)

  // AGC: Load latest proposal ID on mount (only if we haven't set one manually)
  useEffect(() => {
    // AGC: If StakingRevenueShare has proposals, load the latest one
    // AGC: Only auto-load if proposalId is null AND shouldAutoLoadProposal is true
    if (nextProposalIdRevenueShare !== undefined && nextProposalIdRevenueShare > 0n && proposalId === null && shouldAutoLoadProposal) {
      setProposalId(nextProposalIdRevenueShare - 1n)
    }
  }, [nextProposalIdRevenueShare, proposalId, shouldAutoLoadProposal])

  // AGC: Parse proposal data
  const proposalData = proposal ? {
    recipient: proposal[0] as string,
    proposedAt: proposal[1] as bigint,
    votingDeadline: proposal[2] as bigint,
    approved: proposal[3] as boolean,
    executed: proposal[4] as boolean,
    cancelled: proposal[5] as boolean,
    stakerVotesYes: proposal[6] as bigint,
    stakerVotesNo: proposal[7] as bigint,
    tokenType: proposal[8] as number,
    amount: proposal[9] as bigint,
  } : null

  // AGC: Parse canExecute data
  const canExecute = canExecuteData ? canExecuteData[0] as boolean : false
  const executeReason = canExecuteData ? canExecuteData[1] as string : ''

  // AGC: Check if user is a signer
  const isSigner1 = address?.toLowerCase() === FIXED_SIGNER_1.toLowerCase()
  const isSigner2 = address?.toLowerCase() === FIXED_SIGNER_2.toLowerCase()
  const isSigner = isSigner1 || isSigner2

  // AGC: Check if user is a staker
  const isStaker = userStakeBalance && userStakeBalance > 0n

  // AGC: Generate separate proposal ID arrays for each contract (each maintains its own counter)
  const stakingManagerProposalIds = nextProposalIdStakingManager !== undefined && nextProposalIdStakingManager > 0n
    ? Array.from({ length: Number(nextProposalIdStakingManager) }, (_, i) => ({ id: BigInt(i), contract: 'StakingManager' as const })).reverse()
    : []
  
  const revenueShareProposalIds = nextProposalIdRevenueShare !== undefined && nextProposalIdRevenueShare > 0n
    ? Array.from({ length: Number(nextProposalIdRevenueShare) }, (_, i) => ({ id: BigInt(i), contract: 'StakingRevenueShare' as const })).reverse()
    : []

  // AGC: Combine and sort by proposal ID (newest first, but grouped by contract)
  const allProposalIds = [...stakingManagerProposalIds, ...revenueShareProposalIds]
    .sort((a, b) => {
      // AGC: Sort by contract first (StakingRevenueShare first), then by ID (newest first)
      if (a.contract !== b.contract) {
        return a.contract === 'StakingRevenueShare' ? -1 : 1
      }
      return Number(b.id) - Number(a.id)
    })

  // AGC: Format balances from individual reads
  const ethBalance = formatEther(ethBalanceValue || 0n)
  const ogbBalance = formatEther(ogbBalanceRaw ? BigInt(ogbBalanceRaw.toString()) : 0n)
  const usdcBalance = formatUnits(usdcBalanceRaw ? BigInt(usdcBalanceRaw.toString()) : 0n, 6)
  
  // AGC: Show FarmPro only on Mainnet (4th column)
  const isMainnet = chainId === 8453
  const farmProBalance = '0' // AGC: FarmPro balance (only available on mainnet)

  // AGC: V4 Step 1: Propose withdrawal
  const handleProposeWithdrawal = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!isSigner) {
      setError('Only signers can propose withdrawals')
      return
    }

    const durationMinutes = parseInt(votingDurationMinutes) || 5
    if (durationMinutes < 1 || durationMinutes > 10080) {
      setError('Voting duration must be between 1 and 10080 minutes (7 days)')
      return
    }

    // AGC: Each contract maintains its own counter - no need to unify
    const revenueShareNextId = nextProposalIdRevenueShare !== undefined ? nextProposalIdRevenueShare : 0n

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const amount = withdrawAmount === '0' || withdrawAmount === '' 
        ? 0n 
        : tokenType === TokenType.USDC
          ? parseUnits(withdrawAmount, 6)
          : parseEther(withdrawAmount)

      logger.info('V4 proposeWithdrawal (StakingRevenueShare):', {
        recipient: selectedRecipient,
        tokenType,
        amount: amount.toString(),
        votingDurationMinutes: durationMinutes,
        expectedProposalId: revenueShareNextId.toString(), // AGC: This is what the contract will assign (SRS #X)
      })

      const hash = await writeContractAsync({
        address: contractAddress,
        abi: STAKING_REVENUE_SHARE_V4_ABI,
        functionName: 'proposeWithdrawal',
        args: [
          selectedRecipient as `0x${string}`,
          tokenType,
          amount,
          BigInt(durationMinutes),
        ],
      })

      logger.info('Proposal submitted:', hash)
      
      // AGC: The contract will assign the proposal ID based on its own counter (SRS #X)
      const contractAssignedId = revenueShareNextId
      
      setSuccess(`Proposal SRS #${contractAssignedId} submitted! TX: ${hash}`)

      // AGC: Wait for transaction to be mined
      await new Promise(resolve => setTimeout(resolve, 3000))
      
      // AGC: Set the proposal ID that the contract actually assigned
      setProposalId(contractAssignedId)
      setShouldAutoLoadProposal(true) // AGC: Re-enable auto-loading after creating new proposal
      
      // AGC: Refetch all data to update unified counter
      await refetchNextProposalId()
      await refetchProposal()
    } catch (err: any) {
      logger.error('Failed to propose withdrawal:', err)
      setError(err.message || 'Failed to propose withdrawal')
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: V4 Step 2: Approve withdrawal and start voting
  const handleApproveWithdrawal = async () => {
    if (!isConnected || !address || !isSigner) {
      setError('You must be a signer to approve')
      return
    }

    if (proposalId === null) {
      setError('No proposal to approve')
      return
    }

    const durationMinutes = parseInt(votingDurationMinutes) || 5
    if (durationMinutes < 1 || durationMinutes > 10080) {
      setError('Voting duration must be between 1 and 10080 minutes (7 days)')
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const hash = await writeContractAsync({
        address: contractAddress,
        abi: STAKING_REVENUE_SHARE_V4_ABI,
        functionName: 'approveWithdrawal',
        args: [proposalId, BigInt(durationMinutes)],
      })

      logger.info('Approval submitted:', hash)
      setSuccess(`Approved! Voting started for ${durationMinutes} min. TX: ${hash}`)

      await new Promise(resolve => setTimeout(resolve, 3000))
      refetchProposal()
      refetchCanExecute()
    } catch (err: any) {
      logger.error('Failed to approve:', err)
      setError(err.message || 'Failed to approve')
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: V4 Step 3: Vote
  const handleVote = async (voteYes: boolean) => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!isStaker) {
      setError('You must be a staker to vote')
      return
    }

    if (proposalId === null) {
      setError('No proposal to vote on')
      return
    }

    if (hasVoted) {
      setError('You have already voted')
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const hash = await writeContractAsync({
        address: contractAddress,
        abi: STAKING_REVENUE_SHARE_V4_ABI,
        functionName: 'voteOnWithdrawal',
        args: [proposalId, voteYes],
      })

      logger.info('Vote submitted:', hash)
      setSuccess(`Vote submitted! TX: ${hash}`)

      await new Promise(resolve => setTimeout(resolve, 3000))
      refetchProposal()
      refetchVotePassed()
      refetchCanExecute()
    } catch (err: any) {
      logger.error('Failed to vote:', err)
      if (err.message?.includes('Must have stake')) {
        setError('Your stake is not synced. Run sync-stake-to-revenue-share.cjs script.')
      } else {
        setError(err.message || 'Failed to vote')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: Cancel proposal (signer only, can cancel at any step before execution)
  const handleCancelProposal = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!isSigner) {
      setError('Only signers can cancel proposals')
      return
    }

    if (proposalId === null) {
      setError('No proposal to cancel')
      return
    }

    if (proposalData?.executed) {
      setError('Cannot cancel an executed proposal')
      return
    }

    if (proposalData?.cancelled) {
      setError('Proposal is already cancelled')
      return
    }

    // AGC: Confirm cancellation
    if (!confirm('Are you sure you want to cancel this proposal? This action cannot be undone.')) {
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const hash = await writeContractAsync({
        address: contractAddress,
        abi: STAKING_REVENUE_SHARE_V4_ABI,
        functionName: 'cancelProposal',
        args: [proposalId],
      })

      logger.info('Proposal cancelled:', hash)
      setSuccess(`Proposal cancelled! TX: ${hash}`)

      await new Promise(resolve => setTimeout(resolve, 3000))
      
      // AGC: Reset state to allow new proposal
      setProposalId(null)
      setExecutionTxHash(null)
      setWithdrawAmount('0')
      setVotingDurationMinutes('5')
      setTokenType(TokenType.ETH)
      setSelectedRecipient(FIXED_SIGNER_1)
      setShouldAutoLoadProposal(false) // AGC: Prevent auto-loading after cancel
      
      // AGC: Refetch data
      await refetchNextProposalId()
      await refetchProposal()
    } catch (err: any) {
      logger.error('Failed to cancel proposal:', err)
      setError(err.message || 'Failed to cancel proposal')
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: V4 Step 4: Execute (after vote passes)
  const handleExecuteWithdrawal = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!isSigner) {
      setError('Only signers can execute')
      return
    }

    if (proposalId === null) {
      setError('No proposal to execute')
      return
    }

    if (!canExecute) {
      setError(`Cannot execute: ${executeReason}`)
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const hash = await writeContractAsync({
        address: contractAddress,
        abi: STAKING_REVENUE_SHARE_V4_ABI,
        functionName: 'executeWithdrawal',
        args: [proposalId],
      })

      logger.info('Withdrawal executed:', hash)
      setExecutionTxHash(hash)
      
      // AGC: Store tx hash in localStorage for proposal history (shared with StakingManager)
      const updatedHashes = { ...proposalTxHashes, [proposalId.toString()]: hash }
      setProposalTxHashes(updatedHashes)
      localStorage.setItem('emergencyWithdrawProposalTxHashes', JSON.stringify(updatedHashes))
      
      setSuccess(`Withdrawal executed! TX: ${hash}`)

      await new Promise(resolve => setTimeout(resolve, 3000))
      refetchBalances()
      refetchProposal()
    } catch (err: any) {
      logger.error('Failed to execute:', err)
      setError(err.message || 'Failed to execute withdrawal')
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: Calculate vote percentages
  const totalVotes = proposalData ? proposalData.stakerVotesYes + proposalData.stakerVotesNo : 0n
  const yesPercentage = totalVotes > 0n && proposalData
    ? Number((proposalData.stakerVotesYes * 100n) / totalVotes)
    : 0

  // AGC: Determine current step
  const getCurrentStep = () => {
    // AGC: Check proposalId !== null (not !proposalId) because 0n is falsy but valid
    if (proposalId === null || !proposalData) return 1
    if (proposalData.executed) return 5
    if (proposalData.cancelled) return 0
    if (!proposalData.approved) return 2
    if (proposalData.votingDeadline > 0n && BigInt(Date.now()) / 1000n <= proposalData.votingDeadline) return 3
    if (canExecute) return 4
    return 3 // Still in voting or vote failed
  }

  const currentStep = getCurrentStep()
  const steps = [
    { num: 1, label: 'Propose', icon: '📝' },
    { num: 2, label: 'Approve', icon: '✅' },
    { num: 3, label: 'Vote', icon: '🗳️' },
    { num: 4, label: 'Execute', icon: '🚀' },
    { num: 5, label: 'Done', icon: '🎉' },
  ]

  // AGC: Calculate remaining time
  const getRemainingTime = () => {
    if (!proposalData || proposalData.votingDeadline === 0n) return null
    const remaining = Number(proposalData.votingDeadline) * 1000 - Date.now()
    if (remaining <= 0) return 'Ended'
    const minutes = Math.floor(remaining / 60000)
    const seconds = Math.floor((remaining % 60000) / 1000)
    return `${minutes}m ${seconds}s`
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white" style={{ paddingTop: 'env(safe-area-inset-top, 0)', paddingBottom: 'env(safe-area-inset-bottom, 0)' }}>
      <div className="max-w-md mx-auto px-4 pb-20">
        {/* AGC: Header */}
        <div className="flex items-center justify-between pt-4 pb-3 border-b border-white/10">
          <h1 className="text-xl font-bold text-red-400">🚨 Emergency Withdraw V4</h1>
          <button
            onClick={onBack}
            className="px-3 py-1.5 bg-slate-700/50 hover:bg-slate-600 rounded-lg text-sm transition-colors"
          >
            ← Back
          </button>
        </div>

        {/* AGC: Error/Success Messages */}
        {(error || success) && (
          <div className={`mt-3 p-2.5 rounded-lg text-xs ${error ? 'bg-red-900/30 border border-red-500/50' : 'bg-green-900/30 border border-green-500/50'}`}>
            <p className={error ? 'text-red-200' : 'text-green-200'}>
              {error ? '❌' : '✅'} {error || success}
            </p>
          </div>
        )}

        {/* AGC: Step Progress */}
        <div className="mt-4 mb-4">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
            <span>Step {currentStep} of 5</span>
            <span>{steps.find(s => s.num === currentStep)?.label || 'Complete'}</span>
          </div>
          <div className="flex gap-1">
            {steps.map((step) => (
              <div
                key={step.num}
                className={`flex-1 h-1.5 rounded-full transition-all ${
                  step.num < currentStep
                    ? 'bg-green-500'
                    : step.num === currentStep
                    ? currentStep === 5 ? 'bg-green-500' : 'bg-yellow-500'  // AGC: Step 5 = done = green
                    : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
        </div>

        {/* AGC: Balances Grid */}
        <div className="mt-4 pb-3 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm uppercase tracking-wider text-slate-400">💰 Contract Funds</h2>
              <span className="text-xs text-slate-500">(StakingRevenueShare)</span>
              <a
                href={`${chainId === 84532 ? 'https://sepolia.basescan.org' : 'https://basescan.org'}/address/${contractAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 text-xs"
                title={`View StakingRevenueShare contract on BaseScan`}
              >
                🔗
              </a>
            </div>
            <button onClick={() => refetchBalances()} className="text-xs text-slate-400 hover:text-white">🔄</button>
          </div>
          <div className={`grid ${isMainnet ? 'grid-cols-4' : 'grid-cols-3'} gap-2`}>
            <div className="text-center">
              <p className="text-xs text-slate-500">ETH</p>
              <p className="text-lg font-bold">{parseFloat(ethBalance).toFixed(4)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500">OGB</p>
              <p className="text-lg font-bold">{parseFloat(ogbBalance).toFixed(2)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500">USDC</p>
              <p className="text-lg font-bold">{parseFloat(usdcBalance).toFixed(2)}</p>
            </div>
            {isMainnet && (
              <div className="text-center">
                <p className="text-xs text-slate-500">FarmPro</p>
                <p className="text-lg font-bold">{parseFloat(farmProBalance).toFixed(2)}</p>
              </div>
            )}
          </div>
        </div>

        {/* AGC: User Status */}
        {address && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-2">👤 Your Status</h2>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-slate-500">Role</p>
                <p className="text-sm font-bold">
                  {isSigner ? '🔐 Signer' : isStaker ? '💰 Staker' : '👤 User'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Stake</p>
                <p className={`text-sm font-bold ${isStaker ? 'text-green-400' : 'text-slate-500'}`}>
                  {isStaker ? `${formatEther(userStakeBalance || 0n)} OGB` : '0 OGB'}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">{address.slice(0, 6)}...{address.slice(-4)}</p>
          </div>
        )}

        {/* AGC: Step 1 - Propose Form (Signer Only) */}
        {currentStep === 1 && isSigner && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">📝 Step 1: Propose Withdrawal</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Token</label>
                <select
                  value={tokenType}
                  onChange={(e) => setTokenType(Number(e.target.value) as TokenType)}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white"
                >
                  <option value={TokenType.ETH}>ETH</option>
                  <option value={TokenType.OGB}>OGB</option>
                  <option value={TokenType.USDC}>USDC</option>
                  <option value={TokenType.FARMPRO}>FarmPro</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Amount (0 = all)</label>
                <input
                  type="text"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Recipient (Signer Only)</label>
                <select
                  value={selectedRecipient}
                  onChange={(e) => setSelectedRecipient(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white"
                >
                  <option value={FIXED_SIGNER_1}>Signer 1 ({FIXED_SIGNER_1.slice(0, 6)}...)</option>
                  <option value={FIXED_SIGNER_2}>Signer 2 ({FIXED_SIGNER_2.slice(0, 6)}...)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Voting Duration (minutes)</label>
                <input
                  type="number"
                  value={votingDurationMinutes}
                  onChange={(e) => setVotingDurationMinutes(e.target.value)}
                  placeholder="5"
                  min="1"
                  max="10080"
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white"
                />
              </div>
              <button
                onClick={handleProposeWithdrawal}
                disabled={isLoading || isPending || !isConnected}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
              >
                {isLoading ? '⏳ Proposing...' : '📝 Propose Withdrawal'}
              </button>
            </div>
          </div>
        )}

        {/* AGC: Cancel Proposal Button (Signer Only, visible when there's an active proposal) */}
        {isSigner && proposalId !== null && proposalData && !proposalData.executed && !proposalData.cancelled && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <button
              onClick={handleCancelProposal}
              disabled={isLoading || isPending || !isConnected}
              className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
            >
              {isLoading ? '⏳ Cancelling...' : '❌ Cancel Proposal'}
            </button>
            <p className="text-xs text-slate-500 text-center mt-2">
              Cancel this proposal to create a new one
            </p>
          </div>
        )}

        {/* AGC: Step 2 - Approve (Signer Only) */}
        {currentStep === 2 && isSigner && proposalData && !proposalData.approved && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">✅ Step 2: Approve & Start Voting</h2>
            <div className="mb-3 p-2 bg-slate-800/50 rounded-lg text-xs">
              <p><span className="text-slate-500">Token:</span> {TOKEN_LABELS[proposalData.tokenType as TokenType]}</p>
              <p><span className="text-slate-500">Amount:</span> {proposalData.amount === 0n ? 'All' : formatEther(proposalData.amount)}</p>
              <p><span className="text-slate-500">To:</span> {proposalData.recipient.slice(0, 10)}...</p>
            </div>
            <div className="mb-3">
              <label className="block text-xs text-slate-500 mb-1">Voting Duration (minutes)</label>
              <input
                type="number"
                value={votingDurationMinutes}
                onChange={(e) => setVotingDurationMinutes(e.target.value)}
                placeholder="5"
                min="1"
                max="10080"
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleApproveWithdrawal}
                disabled={isLoading || isPending || !isConnected}
                className="flex-1 py-3 bg-yellow-600 hover:bg-yellow-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
              >
                {isLoading ? '⏳ Approving...' : '✅ Approve'}
              </button>
              <button
                onClick={handleCancelProposal}
                disabled={isLoading || isPending || !isConnected}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
              >
                {isLoading ? '⏳...' : '❌ Cancel'}
              </button>
            </div>
          </div>
        )}

        {/* AGC: Step 3 - Vote (Staker Only) */}
        {currentStep === 3 && proposalData && proposalData.approved && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">🗳️ Step 3: Voting</h2>
            <div className="mb-3 p-2 bg-slate-800/50 rounded-lg text-xs">
              <p><span className="text-slate-500">Token:</span> {TOKEN_LABELS[proposalData.tokenType as TokenType]}</p>
              <p><span className="text-slate-500">Amount:</span> {proposalData.amount === 0n ? 'All' : formatEther(proposalData.amount)}</p>
              <p><span className="text-slate-500">To:</span> {proposalData.recipient.slice(0, 10)}...</p>
              <p className="mt-2"><span className="text-slate-500">Time Left:</span> <span className="text-yellow-400 font-bold">{getRemainingTime()}</span></p>
            </div>
            
            {/* Vote Progress Bar */}
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-green-400">YES: {yesPercentage}%</span>
                <span className="text-red-400">NO: {100 - yesPercentage}%</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all" 
                  style={{ width: `${yesPercentage}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Need 51% YES to pass</p>
            </div>

            {isStaker && !hasVoted && (
              <div className="flex gap-2">
                <button
                  onClick={() => handleVote(true)}
                  disabled={isLoading || isPending || !isConnected}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
                >
                  👍 YES
                </button>
                <button
                  onClick={() => handleVote(false)}
                  disabled={isLoading || isPending || !isConnected}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
                >
                  👎 NO
                </button>
              </div>
            )}

            {hasVoted && (
              <p className="text-center text-green-400 text-sm">✅ You have voted</p>
            )}

            {!isStaker && (
              <p className="text-center text-slate-500 text-sm">Only stakers can vote</p>
            )}

            {/* AGC: Cancel button for signers during voting */}
            {isSigner && (
              <button
                onClick={handleCancelProposal}
                disabled={isLoading || isPending || !isConnected}
                className="w-full mt-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-xs transition-colors"
              >
                {isLoading ? '⏳ Cancelling...' : '❌ Cancel Proposal'}
              </button>
            )}
          </div>
        )}

        {/* AGC: Step 4 - Execute (Signer Only, after vote passes) */}
        {currentStep === 4 && isSigner && canExecute && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">🚀 Step 4: Execute Withdrawal</h2>
            <div className="mb-3 p-2 bg-green-900/20 border border-green-500/30 rounded-lg text-xs">
              <p className="text-green-400 font-bold">✅ Vote Passed!</p>
              <p className="text-slate-300 mt-1">Ready to execute withdrawal</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExecuteWithdrawal}
                disabled={isLoading || isPending || !isConnected}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
              >
                {isLoading ? '⏳ Executing...' : '🚀 Execute'}
              </button>
              <button
                onClick={handleCancelProposal}
                disabled={isLoading || isPending || !isConnected}
                className="flex-1 py-3 bg-slate-600 hover:bg-slate-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
              >
                {isLoading ? '⏳...' : '❌ Cancel'}
              </button>
            </div>
          </div>
        )}

        {/* AGC: Step 5 - Done */}
        {currentStep === 5 && proposalData?.executed && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg text-center">
              <p className="text-green-400 font-bold text-lg">🎉 Withdrawal Complete!</p>
              <p className="text-xs text-slate-400 mt-1">Proposal #{proposalId?.toString()} executed successfully</p>
              {executionTxHash && (
                <a
                  href={`${chainId === 84532 ? 'https://sepolia.basescan.org' : 'https://basescan.org'}/tx/${executionTxHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 text-sm underline mt-2 block"
                >
                  View on BaseScan 🔗
                </a>
              )}
            </div>
            {/* AGC: Button to start new proposal */}
            {isSigner && (
              <button
                onClick={() => {
                  // AGC: Reset state to allow new proposal
                  setProposalId(null)
                  setExecutionTxHash(null)
                  setSuccess(null)
                  setError(null)
                  setWithdrawAmount('0')
                  setVotingDurationMinutes('5')
                  setTokenType(TokenType.ETH)
                  setSelectedRecipient(FIXED_SIGNER_1)
                  setShouldAutoLoadProposal(false) // AGC: Prevent auto-loading when user wants to start new proposal
                  refetchNextProposalId()
                }}
                className="w-full mt-3 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold text-sm transition-colors"
              >
                📝 Start New Proposal
              </button>
            )}
          </div>
        )}

        {/* AGC: Waiting Message for Non-Signers */}
        {!isSigner && (currentStep === 1 || currentStep === 2 || currentStep === 4) && (
          <div className="mt-4 p-3 bg-slate-800/50 border border-slate-700 rounded-lg text-center">
            <p className="text-slate-400 text-sm">⏳ Waiting for signer action...</p>
            <p className="text-xs text-slate-500 mt-1">
              {currentStep === 1 && 'A signer needs to propose a withdrawal'}
              {currentStep === 2 && 'A signer needs to approve the proposal'}
              {currentStep === 4 && 'A signer needs to execute the withdrawal'}
            </p>
          </div>
        )}

        {/* AGC: Proposal History */}
        <div className="mt-4 pb-3 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setIsProposalHistoryExpanded(!isProposalHistoryExpanded)}
              className="flex-1 text-left text-xs uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
            >
              <span>📊 PROPOSAL HISTORY {allProposalIds.length > 0 && `(${allProposalIds.length})`}</span>
            </button>
            <span className="text-lg text-slate-400">{isProposalHistoryExpanded ? '▼' : '▶'}</span>
          </div>
          
          {isProposalHistoryExpanded && (
            <>
              {/* AGC: Debug info for proposal history */}
              <div className="mb-2 p-2 bg-slate-800/30 rounded text-xs text-slate-400">
                <p>SRS Next ID: {nextProposalIdRevenueShare?.toString() || 'Loading...'} ({revenueShareProposalIds.length} proposals)</p>
                <p>SM Next ID: {nextProposalIdStakingManager?.toString() || 'Loading...'} ({stakingManagerProposalIds.length} proposals)</p>
                <p>Total: {allProposalIds.length} proposals</p>
                <p>Stored TX Hashes: {Object.keys(proposalTxHashes).length}</p>
              </div>
              
              <div className="space-y-2 mt-2">
                {allProposalIds.length === 0 ? (
                  <p className="text-xs text-slate-500 text-center py-2">No proposals yet</p>
                ) : (
                  allProposalIds.map((proposal) => {
                    // AGC: Create unique key with contract prefix
                    const uniqueKey = `${proposal.contract}-${proposal.id.toString()}`
                    // AGC: Create unique proposal identifier for localStorage and expansion tracking
                    const proposalKey = `${proposal.contract === 'StakingRevenueShare' ? 'SRS' : 'SM'}-${proposal.id.toString()}`
                    const isExpanded = expandedProposals.has(proposal.id)
                    
                    return (
                      <ProposalItem
                        key={uniqueKey}
                        proposalId={proposal.id}
                        revenueShareAddress={contractAddress}
                        stakingManagerAddress={stakingManagerAddress}
                        chainId={chainId}
                        executionTxHash={proposalTxHashes[proposalKey] || proposalTxHashes[proposal.id.toString()] || (proposal.id === proposalId && proposal.contract === 'StakingRevenueShare' ? executionTxHash : null)}
                        isExpanded={isExpanded}
                        onToggleExpand={() => {
                          const newExpanded = new Set(expandedProposals)
                          if (newExpanded.has(proposal.id)) {
                            newExpanded.delete(proposal.id)
                          } else {
                            newExpanded.add(proposal.id)
                          }
                          setExpandedProposals(newExpanded)
                        }}
                      />
                    )
                  })
                )}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  )
}
